import { Button, Flex, Text } from '@mantine/core';
import { StatusBadge } from '@dealstack/frontend/shared/components/StatusBadge/StatusBadge';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { formatStringDateToUI } from '@dealstack/shared/lib/format/dates/date';
import {
	type DataKYCFileTctWithFile,
	KycFileCertificationStatus,
} from '@dealstack/shared/lib/generated';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';

interface Props {
	kycFile: DataKYCFileTctWithFile;
	isReadOnly: boolean;
	isResending: boolean;
	onResend: () => void;
}

/** DC-825: how a proof was certified, and what the certifier declared, for the reviewer. */
export const CertificationReviewPanel = ({ kycFile, isReadOnly, isResending, onResend }: Props) => {
	const tMipManage = useTranslationMipManage();
	const review = kycFile.certificationReview;
	const status = kycFile.certificationStatus;

	const badge = {
		[KycFileCertificationStatus.CERTIFIED]: {
			color: 'green',
			text: tMipManage('kyc.review.certification.status_certified', {
				defaultValue: 'Certified',
			}),
		},
		[KycFileCertificationStatus.SELF_ATTESTED]: {
			color: 'green',
			text: tMipManage('kyc.review.certification.status_self_attested', {
				defaultValue: 'Self-attested',
			}),
		},
		[KycFileCertificationStatus.PENDING_CERTIFIER]: {
			color: 'yellow',
			text: tMipManage('kyc.review.certification.status_pending', {
				defaultValue: 'Awaiting certifier',
			}),
		},
		[KycFileCertificationStatus.UNCERTIFIED]: {
			color: 'red',
			text: tMipManage('kyc.review.certification.status_uncertified', {
				defaultValue: 'Not certified',
			}),
		},
	}[status];

	const rows: { label: string; value: string | null | undefined }[] = review
		? [
				{
					label: tMipManage('kyc.review.certification.nominated', {
						defaultValue: 'Nominated certifier',
					}),
					value: `${review.certifierName} (${review.certifierEmail})`,
				},
				{
					label: tMipManage('kyc.review.certification.declared_name', {
						defaultValue: 'Name given by certifier',
					}),
					value: review.certifierFullName,
				},
				{
					label: tMipManage('kyc.review.certification.capacity', {
						defaultValue: 'Professional capacity',
					}),
					value: review.professionalCapacity,
				},
				{
					label: tMipManage('kyc.review.certification.address', {
						defaultValue: 'Address',
					}),
					value: review.certifierAddress,
				},
				{
					label: tMipManage('kyc.review.certification.invited_at', {
						defaultValue: 'Invited',
					}),
					value: review.invitedAt ? formatStringDateToUI(review.invitedAt) : null,
				},
				{
					label: tMipManage('kyc.review.certification.certified_at', {
						defaultValue: 'Signed',
					}),
					value: review.certifiedAt ? formatStringDateToUI(review.certifiedAt) : null,
				},
				{
					label: tMipManage('kyc.review.certification.signing_method', {
						defaultValue: 'Signing method',
					}),
					value: review.signingMethod,
				},
				{
					label: tMipManage('kyc.review.certification.wording', {
						defaultValue: 'Certification wording',
					}),
					value: review.certificationText,
				},
			]
		: [];

	return (
		<Flex
			direction="column"
			gap="0.5rem"
			p="0.75rem"
			style={{ border: '1px solid var(--mantine-color-gray-3)', borderRadius: '4px' }}
			data-testid={TEST_ELEMENT_IDS.KYC_REVIEW_CERTIFICATION_PANEL}
			data-status={status}
		>
			<Flex align="center" justify="space-between" gap="0.5rem">
				<Text size="B5Bold">
					{tMipManage('kyc.review.certification.title', {
						defaultValue: 'Certification',
					})}
				</Text>
				<StatusBadge color={badge.color} textColor="dark" text={badge.text} />
			</Flex>
			{status === KycFileCertificationStatus.SELF_ATTESTED && (
				<Text size="sm" c="dark.2">
					{tMipManage('kyc.review.certification.self_attested_detail', {
						defaultValue:
							'The investor confirmed they uploaded an already-certified copy on {{date}}.',
						date: formatStringDateToUI(kycFile.certificationAttestedAt ?? undefined),
					})}
				</Text>
			)}
			{status === KycFileCertificationStatus.UNCERTIFIED && (
				<Text size="sm" c="dark.2">
					{tMipManage('kyc.review.certification.uncertified_detail', {
						defaultValue:
							'This programme needs a certified copy. Ask the investor to certify it, or request the document again.',
					})}
				</Text>
			)}
			{status === KycFileCertificationStatus.PENDING_CERTIFIER && (
				<Text size="sm" c="dark.2">
					{tMipManage('kyc.review.certification.pending_detail', {
						defaultValue: 'You can approve this file once the certifier has signed it.',
					})}
				</Text>
			)}
			{rows
				.filter((row) => !!row.value)
				.map((row) => (
					<Flex key={row.label} direction="column">
						<Text size="xs" c="dark.2">
							{row.label}
						</Text>
						<Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
							{row.value}
						</Text>
					</Flex>
				))}
			{status === KycFileCertificationStatus.PENDING_CERTIFIER && !isReadOnly && (
				<Flex>
					<Button
						size="xs"
						variant="outline"
						color="dark"
						loading={isResending}
						onClick={onResend}
						data-testid={TEST_ELEMENT_IDS.KYC_REVIEW_CERTIFICATION_RESEND}
					>
						{tMipManage('kyc.review.certification.resend', {
							defaultValue: 'Resend invitation',
						})}
					</Button>
				</Flex>
			)}
		</Flex>
	);
};
